/**
 * Reads and writes for screens (docs/52 §5 states):
 * - Loading shows the last known data at once (memory, then IndexedDB) with "Updating" under the title; a
 *   skeleton only on a true first load.
 * - Every read refetches when the app comes back to the foreground or back online.
 * - Writes that need step-up open the step-up sheet and retry once (docs/53 §4.2).
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { ApiError, call, currentTenant, type Body, type CallInput, type Query, type Res, type RouteId } from './api.ts';
import { idb } from './idb.ts';
import { createStore, useStore } from './store.ts';

export const online = createStore<boolean>(typeof navigator === 'undefined' ? true : navigator.onLine);

interface Entry {
  data?: unknown;
  updatedAt?: number;
  error?: ApiError | null;
  inflight?: Promise<unknown> | null;
  hydrated?: boolean;
}

const cache = new Map<string, Entry>();
const subs = new Map<string, Set<() => void>>();
const refreshers = new Set<() => void>();

function keyOf(id: RouteId, input: CallInput<RouteId>): string {
  return `${currentTenant() ?? '-'}|${id}|${JSON.stringify(input.params ?? {})}|${JSON.stringify(input.query ?? {})}`;
}

function notify(key: string) {
  for (const f of subs.get(key) ?? []) f();
}

async function load(id: RouteId, input: CallInput<RouteId>, key: string): Promise<unknown> {
  const e = cache.get(key) ?? {};
  if (e.inflight) return e.inflight;
  const p = call(id, { params: input.params, query: input.query } as never)
    .then((data) => {
      cache.set(key, { data, updatedAt: Date.now(), error: null, hydrated: true });
      void idb.set('cache', key, { data, updatedAt: Date.now() });
      return data;
    })
    .catch((err: unknown) => {
      const cur = cache.get(key) ?? {};
      cache.set(key, { ...cur, inflight: null, error: err instanceof ApiError ? err : new ApiError('internal', 500, String(err)) });
      if (err instanceof ApiError && err.offline) online.set(false);
    })
    .finally(() => notify(key));
  cache.set(key, { ...e, inflight: p });
  notify(key);
  return p;
}

export interface QueryState<T> {
  data: T | undefined;
  /** No data yet and a request is out: the true first load. */
  loading: boolean;
  /** Data shown and a fresher copy is on its way. */
  refreshing: boolean;
  error: ApiError | null;
  updatedAt: number | null;
  refetch: () => Promise<void>;
}

export function useQuery<K extends RouteId>(id: K, input: { params?: CallInput<K>['params']; query?: Query<K> } = {}, opts: { enabled?: boolean } = {}): QueryState<Res<K>> {
  const enabled = opts.enabled !== false;
  const key = keyOf(id, input as CallInput<RouteId>);
  const [, force] = useState(0);
  const inputRef = useRef(input);
  inputRef.current = input;

  useEffect(() => {
    if (!enabled) return;
    const set = subs.get(key) ?? new Set();
    const f = () => force((n) => n + 1);
    set.add(f);
    subs.set(key, set);
    const entry = cache.get(key);
    if (!entry?.hydrated) {
      void idb.get<{ data: unknown; updatedAt: number }>('cache', key).then((hit) => {
        const cur = cache.get(key) ?? {};
        if (hit && cur.data === undefined) cache.set(key, { ...cur, data: hit.data, updatedAt: hit.updatedAt, hydrated: true });
        else cache.set(key, { ...cur, hydrated: true });
        notify(key);
      });
    }
    void load(id, inputRef.current as CallInput<RouteId>, key);
    const refresh = () => void load(id, inputRef.current as CallInput<RouteId>, key);
    refreshers.add(refresh);
    return () => {
      set.delete(f);
      refreshers.delete(refresh);
    };
  }, [key, enabled]);

  const e = cache.get(key) ?? {};
  return {
    data: e.data as Res<K> | undefined,
    loading: e.data === undefined && !e.error,
    refreshing: !!e.inflight && e.data !== undefined,
    error: e.error ?? null,
    updatedAt: e.updatedAt ?? null,
    refetch: () => load(id, inputRef.current as CallInput<RouteId>, key).then(() => undefined),
  };
}

/** Refetch everything on screen (foreground, reconnect, pull to refresh). */
export function refreshAll(): void {
  for (const r of refreshers) r();
}

/** Drop cached reads for these routes so the next view refetches; screens on show refetch now. */
export function invalidate(...ids: RouteId[]): void {
  for (const key of [...cache.keys()]) {
    const id = key.split('|')[1];
    if (ids.includes(id as RouteId)) {
      const e = cache.get(key)!;
      cache.set(key, { ...e, updatedAt: 0 });
    }
  }
  refreshAll();
}

/** Write a cached read directly (optimistic updates and server echoes). */
export function setCached<K extends RouteId>(id: K, input: { params?: CallInput<K>['params']; query?: Query<K> }, update: (prev: Res<K> | undefined) => Res<K> | undefined): void {
  const key = keyOf(id, input as CallInput<RouteId>);
  const e = cache.get(key) ?? {};
  const data = update(e.data as Res<K> | undefined);
  cache.set(key, { ...e, data, updatedAt: Date.now(), hydrated: true });
  void idb.set('cache', key, { data, updatedAt: Date.now() });
  notify(key);
}

export function clearMemoryCache(): void {
  cache.clear();
}

// ------------------------------------------------------------------------------------------ writes

let stepUpHandler: (() => Promise<boolean>) | null = null;
export function setStepUpHandler(h: () => Promise<boolean>): void {
  stepUpHandler = h;
}

let signedOutHandler: (() => void) | null = null;
export function setSignedOutHandler(h: () => void): void {
  signedOutHandler = h;
}

export async function send<K extends RouteId>(id: K, input: { params?: CallInput<K>['params']; query?: Query<K>; body?: Body<K> } = {}): Promise<Res<K>> {
  try {
    return await call(id, input as CallInput<K>);
  } catch (e) {
    if (e instanceof ApiError && e.code === 'step_up_required' && stepUpHandler) {
      if (await stepUpHandler()) return call(id, input as CallInput<K>);
    }
    if (e instanceof ApiError && e.code === 'unauthenticated') signedOutHandler?.();
    if (e instanceof ApiError && e.offline) online.set(false);
    throw e;
  }
}

/** A write with busy and error state for a button. */
export function useSend() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError('internal', 500, String(e)));
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run, setError };
}

export function useOnline(): boolean {
  return useStore(online);
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    online.set(true);
    refreshAll();
  });
  window.addEventListener('offline', () => online.set(false));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshAll();
  });
}
